import fp from "lodash/fp";

import {
	hasPermissions,
	agentPermissionSet,
	hasPermissionsByUserGroup,
	hasPermissionsByUserGroupForHistory,
	merchantPermissionSet,
} from "helpers";
import { AddressCommonType, PartnerTypes, UserNew } from "types";

export const getFullName = (
	user:
		| string
		| { firstName?: string; lastName?: string }
		| { first_name?: string; last_name: string }
		| { fullname?: string }
		| { fullName: string }
		| { name: string }
		| null
		| undefined,
) => {
	if (!user) return "";
	if (typeof user === "string") return user;

	if ("fullname" in user) {
		return user.fullname;
	} else if ("fullName" in user) {
		return user.fullName;
	} else if ("firstName" in user || "lastName" in user) {
		return fp.flow(
			fp.compact,
			fp.join(" "),
		)([user.firstName, user.lastName]);
	} else if ("name" in user) {
		return user.name;
	} else if ("first_name" in user || "last_name" in user) {
		return fp.flow(
			fp.compact,
			fp.join(" "),
		)([user.first_name, user.last_name]);
	} else {
		return "";
	}
};

// TODO: Remove and use useGetUserPageUrl
export const getUserPageUrl = user => {
	if (typeof user !== "object" || !user?.id) return "";

	if (user?.is_agent || user?.isAgent) {
		return hasPermissions(agentPermissionSet)
			? `/system-setup/agent-setup/edit-agent/${user?.id}/general-info`
			: "";
	} else {
		return hasPermissionsByUserGroup(user.role?.name)
			? `/system-setup/users/${user?.id}/general-info`
			: "";
	}
};

export const useGetUserPageUrl = (user: UserNew, id?: number | string) => {
	if (typeof user !== "object") return "";

	if (user?.is_agent || user?.isAgent) {
		return hasPermissions(agentPermissionSet)
			? `/system-setup/agent-setup/edit-agent/${
					id || user?.id
			  }/general-info`
			: "";
	} else {
		return hasPermissionsByUserGroup(user?.role?.name)
			? `/system-setup/users/${id || user?.id}/general-info`
			: "";
	}
};

export const getAgentPageUrl = agent => {
	if (typeof agent !== "object" || !agent?.id) return "";

	return hasPermissions(agentPermissionSet)
		? `/system-setup/agent-setup/edit-agent/${agent?.id}/general-info`
		: "";
};

export const getAgentPageUrlById = id => {
	if (!id) return "";

	return hasPermissions(agentPermissionSet)
		? `/system-setup/agent-setup/edit-agent/${id}/general-info`
		: "";
};

export const useGetUserUrl = (user: UserNew, id?: number | string) => {
	if (typeof user !== "object") return "";

	return hasPermissionsByUserGroup(user?.role?.name)
		? `/system-setup/users/${id || user?.id}/general-info`
		: "";
};

export const getAgentOfficePageUrlById = id => {
	if (!id) return "";

	return hasPermissions(agentPermissionSet)
		? `/system-setup/agent-office-setup/edit-agent-office/${id}/general-info`
		: "";
};

export const getMerchantPageUrlById = id => {
	if (!id) return "";

	return hasPermissions(merchantPermissionSet)
		? `/merchant/list/info/${id}/general`
		: "";
};

export const useGetUserPageUrlForHistory = (
	user: UserNew,
	userHistoryRole: string,
	id?: string,
) => {
	if (typeof user !== "object") return "";

	if (user?.is_agent || user?.isAgent) {
		return hasPermissions(agentPermissionSet)
			? `/system-setup/agent-setup/edit-agent/${
					id || user?.id
			  }/general-info`
			: "";
	} else {
		return hasPermissionsByUserGroupForHistory(userHistoryRole)
			? `/system-setup/users/${id || user?.id}/general-info`
			: "";
	}
};

export const getAddress = (address?: AddressCommonType) => {
	if (!address) return;

	const array = [
		address?.address_line1,
		address?.address_line2,
		address?.city,
		address?.region_code,
		address?.postal_code,
	];

	return array.filter(Boolean).join(", ");
};

const LAST_USER_ACTION_KEY = "lastUserAction";

export const setUserActionDate = () =>
	localStorage.setItem(LAST_USER_ACTION_KEY, new Date().toISOString());

export const getUserActionDate = () => {
	const lastUserAction = localStorage.getItem(LAST_USER_ACTION_KEY);

	return lastUserAction ? new Date(lastUserAction) : undefined;
};

export const removeUserActionDate = () => {
	localStorage.removeItem(LAST_USER_ACTION_KEY);
};

export const isPartnerTypeInsideSales = partnerType =>
	partnerType === PartnerTypes.InsideSalesAgent.value ||
	partnerType === PartnerTypes.InsideSalesManager.value;
